import { Button, ButtonVariantEnum, Chip } from '@Wonder-Cave/ui';
import { FilterDataTypeEnum, FilterOperatorEnum, ISearchClientsRecord, ISearchTcrCampaignsRecord, IUpdateClientRequest, IUpdateClientResponse, SearchSortDirectionEnum } from '@shared/models';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { axiosGet, axiosPut, getQueryParamString } from '../../authAxios';
import useTcrCampaigns from '../../hooks/useTcrCampaigns';
import TCRCampaigns from '../TCRCampaigns/TCRCampaigns';
import { BrandInformation } from './Tabs/BrandInformation';
import { ClientInformation } from './Tabs/ClientInformation';
import { NumberManagement } from './Tabs/NumberManagement/NumberManagement';

export enum ClientDetailsViewEnum {
  CLIENT_INFO = 0,
  BRAND_INFO = 1,
  TCR_CAMPAIGNS = 2,
  NUMBER_MANAGEMENT = 3
}

export interface ClientDetailsProps {
  view?: ClientDetailsViewEnum;
}

const ClientDetails = ({ view: viewProp }: ClientDetailsProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const { clientId } = useParams();
  const [client, setClient] = useState<ISearchClientsRecord>();
  const [view, setView] = useState(viewProp ?? ClientDetailsViewEnum.CLIENT_INFO);

  const [{ data: tcrCampaignsData, loading: tcrCampaignsLoading }] = useTcrCampaigns({
    searchRequest: { pagination: { skip: 0, take: 100 }, sort: { fieldName: 'providerId', sortDirection: SearchSortDirectionEnum.ASC }, filters: [{ fieldName: 'isActive', dataType: FilterDataTypeEnum.BOOLEAN, operator: FilterOperatorEnum.EQUALS, value: true }] },
    clientId,
  });
  const allTcrCampaigns: ISearchTcrCampaignsRecord[] = tcrCampaignsData?.records ?? [];

  const getClient = async () => {
    setLoading(true);
    try {
      const response = await axiosGet<ISearchClientsRecord>(`/clients/${clientId}?${getQueryParamString({ environment: location?.state?.environment })}`);
      if (response.status === 200) {
        setClient(response?.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateClient = async (request: Omit<IUpdateClientRequest, 'id'>) => {
    await axiosPut<IUpdateClientResponse>(`/clients/${clientId}`, request);
  };

  const setViewComponent = (view: ClientDetailsViewEnum) => {
    switch (view) {
      case ClientDetailsViewEnum.CLIENT_INFO:
        navigate(`/clients/${clientId}/info`);
        setView(view);
        break;
      case ClientDetailsViewEnum.BRAND_INFO:
        navigate(`/clients/${clientId}/brand`);
        setView(view);
        break;
      case ClientDetailsViewEnum.TCR_CAMPAIGNS:
        navigate(`/clients/${clientId}/tcr`);
        setView(view);
        break;
      case ClientDetailsViewEnum.NUMBER_MANAGEMENT:
        navigate(`/clients/${clientId}/numbers`);
        setView(view);
        break;
    }
  };

  useEffect(() => {
    if (clientId) {
      getClient();
    }
  }, []);

  return <div>
    <div className='px-20'>
      <div className='flex justify-between'>
        <h1 className='mb-2'>{client?.name}</h1>
        <div className='space-x-2'>
          <Button disabled={loading} onClick={() => navigate('/clients/add', {
            state: {
              ...client,
              name: client?.name + " (Clone)",
              privacyPolicyUrl: client?.privacyPolicyUrl ?? undefined,
              termsAndConditionsUrl: client?.termsAndConditionsUrl ?? undefined,
              defaultClickerGroupId: client?.defaultClickerGroupId ?? undefined
            }
          })} variant={ButtonVariantEnum.PRIMARY}>DUPLICATE</Button>
          <Button onClick={() => navigate('/clients')} variant={ButtonVariantEnum.SECONDARY}>BACK</Button>
        </div>
      </div>
      <div className='flex mb-8 text-wc-blue'><h3 className='mr-2'>{'Brand ID: ' + client?.tcrBrandId}</h3> | <h3 className='ml-2'>{'Universal EIN: ' + client?.tcrBrand?.universalEin}</h3></div>
    </div>
    <div className="flex items-center px-20 mb-8 space-x-4">
      <h4 className="text-dark-gray">View:</h4>
      <Chip active={view === ClientDetailsViewEnum.CLIENT_INFO} color='bg-turquoise' onClick={() => setViewComponent(ClientDetailsViewEnum.CLIENT_INFO)}>
        Client Info
      </Chip>
      <Chip active={view === ClientDetailsViewEnum.BRAND_INFO} color='bg-turquoise' onClick={() => setViewComponent(ClientDetailsViewEnum.BRAND_INFO)}>
        Brand Info
      </Chip>
      <Chip active={view === ClientDetailsViewEnum.TCR_CAMPAIGNS} color='bg-turquoise' onClick={() => setViewComponent(ClientDetailsViewEnum.TCR_CAMPAIGNS)}>
        TCR Campaigns
      </Chip>
      <Chip active={view === ClientDetailsViewEnum.NUMBER_MANAGEMENT} color='bg-turquoise' onClick={() => setViewComponent(ClientDetailsViewEnum.NUMBER_MANAGEMENT)}>
        Number Management
      </Chip>
    </div>
    {view === ClientDetailsViewEnum.CLIENT_INFO && <ClientInformation client={client} loading={loading} />}
    {view === ClientDetailsViewEnum.BRAND_INFO && <BrandInformation client={client} />}
    {view === ClientDetailsViewEnum.TCR_CAMPAIGNS && <TCRCampaigns client={client} />}
    {view === ClientDetailsViewEnum.NUMBER_MANAGEMENT && <NumberManagement client={client} clientLoading={loading} allTcrCampaigns={allTcrCampaigns} tcrCampaignsLoading={tcrCampaignsLoading} />}
  </div>;
};

export default ClientDetails;
